// ============================================================
// miniMarkdown (v8.363.9) - the subset a handout note needs, as an AST.
//
// Headings (#, ##, ###), paragraphs, unordered and ordered lists, **bold**,
// *italic*, `code` and [links](url). No raw HTML, ever: the note is text a
// GM typed, rendered by React, and a link opens in a new tab with
// rel="noopener". Twenty lines of parsing beat a dependency that would
// also parse HTML.
// ============================================================
export type Inline =
  | { t: 'text'; v: string }
  | { t: 'bold'; v: string }
  | { t: 'italic'; v: string }
  | { t: 'code'; v: string }
  | { t: 'link'; v: string; href: string };
export type Block =
  | { t: 'heading'; level: 1 | 2 | 3; inlines: Inline[] }
  | { t: 'paragraph'; inlines: Inline[] }
  | { t: 'list'; ordered: boolean; items: Inline[][] };

const SAFE_HREF = /^(https?:\/\/|mailto:|#)/i;

/** One match of the inline pattern, as the inline it spells; a link with an unsafe href is plain text. */
function inlineOf(m: RegExpExecArray): Inline | null {
  if (m[2] !== undefined) return { t: 'bold', v: m[2] };
  if (m[4] !== undefined) return { t: 'italic', v: m[4] };
  if (m[6] !== undefined) return { t: 'code', v: m[6] };
  if (m[8] === undefined) return null;
  const href = m[9];
  return SAFE_HREF.test(href) ? { t: 'link', v: m[8], href } : { t: 'text', v: m[8] };
}

export function parseInlines(text: string): Inline[] {
  const out: Inline[] = [];
  const re = /(\*\*(.+?)\*\*)|(\*(.+?)\*)|(`(.+?)`)|(\[([^\]]+)\]\(([^)\s]+)\))/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push({ t: 'text', v: text.slice(last, m.index) });
    const inline = inlineOf(m);
    if (inline) out.push(inline);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push({ t: 'text', v: text.slice(last) });
  return out;
}

const HEADING = /^(#{1,3})\s+(.*)$/;
const BULLET = /^\s*[-*]\s+(.*)$/;
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/;

/** A list line's kind and text, or null for any other line. */
function listItemOf(line: string): { ordered: boolean; text: string } | null {
  const ul = line.match(BULLET);
  if (ul) return { ordered: false, text: ul[1] };
  const ol = line.match(NUMBERED);
  return ol ? { ordered: true, text: ol[1] } : null;
}

/** Accumulates blocks line by line: an open paragraph and an open list, flushed at their boundaries. */
class BlockBuilder {
  readonly blocks: Block[] = [];
  private para: string[] = [];
  private list: { ordered: boolean; items: string[] } | null = null;

  private flushPara(): void {
    if (this.para.length) this.blocks.push({ t: 'paragraph', inlines: parseInlines(this.para.join(' ')) });
    this.para = [];
  }

  private flushList(): void {
    const list = this.list;
    if (list) this.blocks.push({ t: 'list', ordered: list.ordered, items: list.items.map(parseInlines) });
    this.list = null;
  }

  flush(): void {
    this.flushPara();
    this.flushList();
  }

  heading(level: number, text: string): void {
    this.flush();
    this.blocks.push({ t: 'heading', level: level as 1 | 2 | 3, inlines: parseInlines(text) });
  }

  listItem(ordered: boolean, text: string): void {
    this.flushPara();
    if (!this.list || this.list.ordered !== ordered) {
      this.flushList();
      this.list = { ordered, items: [] };
    }
    this.list.items.push(text);
  }

  paragraphLine(text: string): void {
    this.flushList();
    this.para.push(text);
  }
}

function addLine(b: BlockBuilder, raw: string): void {
  const line = raw.trimEnd();
  if (!line.trim()) return b.flush();
  const h = line.match(HEADING);
  if (h) return b.heading(h[1].length, h[2]);
  const item = listItemOf(line);
  if (item) return b.listItem(item.ordered, item.text);
  b.paragraphLine(line.trim());
}

export function parseMarkdown(src: string): Block[] {
  const b = new BlockBuilder();
  for (const raw of src.replace(/\r\n?/g, '\n').split('\n')) addLine(b, raw);
  b.flush();
  return b.blocks;
}
